// import { createContext, useContext, useEffect, useState } from "react";
// import { getCart, addToCartAPI, deleteCart } from "../api/cartApi";
// import { useAuth } from "./AuthContext";
// import { toast } from "react-toastify";

// const CartContext = createContext();
// export const useCart = () => useContext(CartContext);

// export const CartProvider = ({ children }) => {
//   const { isLoggedIn, token } = useAuth(); // 🔥 take token also
//   const [cartItems, setCartItems] = useState([]);
//   const [isLoading, setIsLoading] = useState(true);

//   // 🔥 SINGLE cart initializer (shared)
//   const initializeCart = async () => {
//     setIsLoading(true);
//     try {
//       if (isLoggedIn && token) {
//         const serverCart = await getCart(token);
//         setCartItems(serverCart || []);
//       } else {
//         const guestCart = JSON.parse(
//           sessionStorage.getItem("guestCart") || "[]"
//         );
//         setCartItems(guestCart);
//       }
//     } catch (error) {
//       setCartItems([]);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   // ✅ INIT cart ONLY when token is ready
//   useEffect(() => {
//     if (isLoggedIn && !token) return; // ⛔ wait for token
//     initializeCart();
//   }, [isLoggedIn, token]);

//   // ✅ Sync guest cart on login
//   useEffect(() => {
//     const syncCartOnLogin = async () => {
//       if (!isLoggedIn || !token) return;

//       const guestCart = JSON.parse(
//         sessionStorage.getItem("guestCart") || "[]"
//       );
//       if (guestCart.length === 0) return;

//       try {
//         await Promise.all(
//           guestCart.map((item) => addToCartAPI(item.product_id))
//         );

//         sessionStorage.removeItem("guestCart");

//         const updatedCart = await getCart(token);
//         setCartItems(updatedCart || []);
//       } catch (error) {
//         console.error("Cart sync failed", error);
//       }
//     };

//     syncCartOnLogin();
//   }, [isLoggedIn, token]);

//   // ➕ Add to cart
//   const addToCart = async (product) => {
//     const exists = cartItems.some(
//       (item) => item.product_id === product.product_id
//     );

//     if (exists) {
//       toast.info("Product already in cart", {
//         position: "bottom-right",
//         autoClose: 2000,
//       });
//       return { status: "exists" };
//     }

//     if (isLoggedIn && token) {
//       try {
//         await addToCartAPI(product.product_id);
//         const updatedCart = await getCart(token);
//         setCartItems(updatedCart || []);
//         return { status: "added" };
//       } catch {
//         toast.error("Failed to add product");
//         return { status: "error" };
//       }
//     } else {
//       const newItem = {
//         product_id: product.product_id,
//         courseName: product.product_name,
//         courseImage: product.image,
//         coursePrice: product.price,
//       };

//       const newCart = [...cartItems, newItem];
//       setCartItems(newCart);
//       sessionStorage.setItem("guestCart", JSON.stringify(newCart));
//       return { status: "added" };
//     }
//   };

//   // ➖ Remove from cart
//   const removeFromCart = async (product_id) => {
//     if (isLoggedIn && token) {
//       try {
//         await deleteCart(product_id);
//         const updatedCart = await getCart(token);
//         setCartItems(updatedCart || []);
//       } catch {
//         toast.error("Failed to remove item");
//       }
//     } else {
//       const newCart = cartItems.filter(
//         (item) => item.product_id !== product_id
//       );
//       setCartItems(newCart);
//       sessionStorage.setItem("guestCart", JSON.stringify(newCart));
//     }
//   };

//   // 🧹 Clear cart
//   const clearCart = () => {
//     setCartItems([]);
//     if (!isLoggedIn) {
//       sessionStorage.removeItem("guestCart");
//     }
//   };

//   return (
//     <CartContext.Provider
//       value={{
//         cartItems,
//         isLoading,
//         addToCart,
//         clearCart,
//         removeCartItem: removeFromCart,
//         cartCount: cartItems.length,
//       }}
//     >
//       {children}
//     </CartContext.Provider>
//   );
// };


import { createContext, useContext, useEffect, useState } from "react";
import { getCart, addToCartAPI, deleteCart } from "../api/cartApi";
import { useAuth } from "./AuthContext";
import { toast } from "react-toastify";

const CartContext = createContext();
export const useCart = () => useContext(CartContext);

/* 🧠 Helper: ALWAYS return array */
const normalizeCart = (res) => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  return [];
};

export const CartProvider = ({ children }) => {
  const { isLoggedIn, token } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  /* 🔥 INIT CART */
  const initializeCart = async () => {
    setIsLoading(true);
    try {
      if (isLoggedIn && token) {
        const serverCart = await getCart(token);
        setCartItems(normalizeCart(serverCart));
      } else {
        const guestCart = JSON.parse(
          sessionStorage.getItem("guestCart") || "[]"
        );
        setCartItems(Array.isArray(guestCart) ? guestCart : []);
      }
    } catch (error) {
      console.error("Init cart error:", error);
      setCartItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  /* ⏳ Wait for token */
  useEffect(() => {
    if (isLoggedIn && !token) return;
    initializeCart();
  }, [isLoggedIn, token]);

  /* 🔄 Sync guest cart on login */
  useEffect(() => {
    const syncCartOnLogin = async () => {
      if (!isLoggedIn || !token) return;

      const guestCart = JSON.parse(
        sessionStorage.getItem("guestCart") || "[]"
      );

      if (!Array.isArray(guestCart) || guestCart.length === 0) return;

      try {
        await Promise.all(
          guestCart.map((item) => addToCartAPI(item.product_id))
        );

        sessionStorage.removeItem("guestCart");

        const updatedCart = await getCart(token);
        setCartItems(normalizeCart(updatedCart));
      } catch (error) {
        console.error("Cart sync failed:", error);
      }
    };

    syncCartOnLogin();
  }, [isLoggedIn, token]);

  /* ➕ ADD TO CART */
  const addToCart = async (product) => {
    const safeCart = Array.isArray(cartItems) ? cartItems : [];

    const exists = safeCart.some(
      (item) => item.product_id === product.product_id
    );

    if (exists) {
      toast.info("Product already in cart", {
        position: "bottom-right",
        autoClose: 2000,
      });
      return { status: "exists" };
    }

    if (isLoggedIn && token) {
      try {
        await addToCartAPI(product.product_id);
        const updatedCart = await getCart(token);
        setCartItems(normalizeCart(updatedCart));
        return { status: "added" };
      } catch (error) {
        toast.error("Failed to add product");
        return { status: "error" };
      }
    } else {
      const newItem = {
        product_id: product.product_id,
        courseName: product.product_name,
        courseImage: product.image,
        coursePrice: product.price,
      };

      const newCart = [...safeCart, newItem];
      setCartItems(newCart);
      sessionStorage.setItem("guestCart", JSON.stringify(newCart));
      return { status: "added" };
    }
  };

  /* ➖ REMOVE FROM CART */
  const removeFromCart = async (product_id) => {
    const safeCart = Array.isArray(cartItems) ? cartItems : [];

    if (isLoggedIn && token) {
      try {
        await deleteCart(product_id);
        const updatedCart = await getCart(token);
        setCartItems(normalizeCart(updatedCart));
      } catch {
        toast.error("Failed to remove item");
      }
    } else {
      const newCart = safeCart.filter(
        (item) => item.product_id !== product_id
      );
      setCartItems(newCart);
      sessionStorage.setItem("guestCart", JSON.stringify(newCart));
    }
  };

  /* 🧹 CLEAR CART */
  const clearCart = () => {
    setCartItems([]);
    if (!isLoggedIn) {
      sessionStorage.removeItem("guestCart");
    }
  };

  return (
    <CartContext.Provider
      value={{
        cartItems: Array.isArray(cartItems) ? cartItems : [],
        isLoading,
        addToCart,
        removeCartItem: removeFromCart,
        clearCart,
        cartCount: Array.isArray(cartItems) ? cartItems.length : 0,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
